/**
 * Full-day hourly grid: 00:00–01:00 … 23:00–00:00 (24 rows).
 */
export function buildFullDayHourlySlots() {
  const slots = [];
  for (let h = 0; h < 24; h += 1) {
    const start = `${String(h).padStart(2, "0")}:00`;
    const end = h === 23 ? "00:00" : `${String(h + 1).padStart(2, "0")}:00`;
    slots.push({ start, end });
  }
  return slots;
}

/**
 * Labels for read-only timetable view: value "HH:mm-HH:mm" for grid keys.
 */
export function buildViewTimeSlotOptions() {
  return buildFullDayHourlySlots().map(({ start, end }) => ({
    label: `${start} – ${end}`,
    value: `${start}-${end}`,
  }));
}

/**
 * Rows for timetable detail view from saved schedule (school hours + breaks).
 * Matches keys used when placing slots: `${start}-${end}` with HH:mm parts.
 * @returns {Array<{label:string,value:string,slotType:'lecture'|'break',breakLabel?:string}>}|null
 */
export function buildViewSlotsFromSchedule(slotsPerDay) {
  if (!Array.isArray(slotsPerDay) || slotsPerDay.length === 0) return null;
  return slotsPerDay.map((s) => {
    const start = String(s.start ?? "").slice(0, 5);
    const end = String(s.end ?? "").slice(0, 5);
    const isBreak = String(s.type ?? "").toLowerCase() === "break";
    return {
      label: `${start} – ${end}`,
      value: `${start}-${end}`,
      slotType: isBreak ? "break" : "lecture",
      breakLabel: s.label,
    };
  });
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

export const IST_TIMEZONE = "Asia/Kolkata";

/** Parse datetime-local / API value as IST wall clock (matches backend parseDateTimeLocalAsIST). */
export function parseDateTimeLocalAsIST(value) {
  if (value == null || value === "") return null;
  const s = String(value).trim();
  if (!s) return null;
  if (/[zZ]$/.test(s) || /[+-]\d{2}:?\d{2}$/.test(s)) {
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  const normalized = s.length === 16 ? `${s}:00` : s;
  const d = new Date(`${normalized}+05:30`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Display stored UTC instant in IST (e.g. 4 Jun 2026, 12:36 pm). */
export function formatDateTimeIST(value) {
  if (value == null || value === "") return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: IST_TIMEZONE,
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(d);
}

/** Value for `<input type="datetime-local" />` from stored ISO. */
export function utcIsoToDatetimeLocalIST(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(d);
  const get = (type) => parts.find((p) => p.type === type)?.value || "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}`;
}

/** Calendar date YYYY-MM-DD in Asia/Kolkata. */
export function formatDateIST(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** Wall-clock parts for a timestamp in IST. */
export function getISTTimeParts(value = new Date()) {
  const d = value instanceof Date ? value : new Date(value);
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: IST_TIMEZONE,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(Number.isNaN(d.getTime()) ? new Date() : d);
  const get = (type) =>
    Number.parseInt(parts.find((p) => p.type === type)?.value || "0", 10);
  const hour = get("hour");
  const minute = get("minute");
  const second = get("second");
  return {
    hour,
    minute,
    second,
    minutesOfDay: hour * 60 + minute,
    secondsOfDay: hour * 3600 + minute * 60 + second,
  };
}

/** Seconds since IST midnight from API/Sequelize time value. */
export function timeValueToSecondsOfDay(timeValue) {
  const hhmm = toTimeInputValue(timeValue);
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map((x) => Number.parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 3600 + m * 60;
}

/**
 * True when `now` (IST) is on the schedule date and at/after lecture start (same-day legacy window).
 * Teachers can take attendance any time after the slot starts until the IST day ends.
 */
export function isSlotActiveNowIST({ date, startTime, endTime, now = new Date() }) {
  const dateYmd = String(date || "").slice(0, 10);
  if (!dateYmd || dateYmd !== formatDateIST(now)) return false;
  const nowSec = getISTTimeParts(now).secondsOfDay;
  const startSec = timeValueToSecondsOfDay(startTime);
  if (startSec == null) return false;
  return nowSec >= startSec;
}

/** True during the live lecture window [start, end + buffer] in IST. */
export function isSlotInLiveLectureWindowIST({
  date,
  startTime,
  endTime,
  bufferMinutes = 15,
  now = new Date(),
}) {
  const dateYmd = String(date || "").slice(0, 10);
  if (!dateYmd || dateYmd !== formatDateIST(now)) return false;
  const nowSec = getISTTimeParts(now).secondsOfDay;
  const startSec = timeValueToSecondsOfDay(startTime);
  const endSec = timeValueToSecondsOfDay(endTime);
  if (startSec == null || endSec == null) return false;
  return nowSec >= startSec && nowSec <= endSec + bufferMinutes * 60;
}

function timeToMinutes(hhmm) {
  if (!hhmm || typeof hhmm !== "string") return 0;
  const s = hhmm.trim().slice(0, 5);
  const [h, m] = s.split(":").map((x) => Number.parseInt(x, 10));
  if (Number.isNaN(h) || Number.isNaN(m)) return 0;
  return h * 60 + m;
}

function minutesToTime(mins) {
  const m = Math.max(0, Math.floor(mins));
  const h = Math.floor(m / 60) % 24;
  const min = m % 60;
  return `${pad2(h)}:${pad2(min)}`;
}

function intervalsOverlap(a0, a1, b0, b1) {
  return a0 < b1 && b0 < a1;
}

/**
 * Same rules as backend `buildSlotGrid` — rebuilds period rows + breaks when only template fields exist.
 */
export function buildSlotGridFromConfig(opts = {}) {
  const dayStart = opts.dayStart || "09:00";
  const dayEnd = opts.dayEnd || "22:00";
  const slotDurationMinutes = Math.max(1, Math.min(600, Number.parseInt(String(opts.slotDurationMinutes || 60), 10) || 60));
  const breaks =
    Array.isArray(opts.breaks) && opts.breaks.length > 0
      ? opts.breaks.map((b) => ({
          start: String(b.start || "").slice(0, 5),
          end: String(b.end || "").slice(0, 5),
          label: b.label || "Break",
        }))
      : [{ start: "12:00", end: "13:00", label: "Lunch" }];

  // Sort breaks by start time
  const sortedBreaks = [...breaks].sort((a, b) => timeToMinutes(a.start) - timeToMinutes(b.start));

  let currentTime = timeToMinutes(dayStart);
  const endM = timeToMinutes(dayEnd);
  const allSlots = [];
  let breakIndex = 0;

  while (currentTime < endM) {
    // Check if we've reached a break
    if (breakIndex < sortedBreaks.length) {
      const br = sortedBreaks[breakIndex];
      const breakStart = timeToMinutes(br.start);
      const breakEnd = timeToMinutes(br.end);

      // If current time is at or past break start, insert the break slot
      if (currentTime >= breakStart && currentTime < breakEnd) {
        allSlots.push({
          start: minutesToTime(currentTime),
          end: minutesToTime(breakEnd),
          type: "break",
          label: br.label,
        });
        currentTime = breakEnd;
        breakIndex++;
        continue;
      }

      // If the next lecture slot would overlap with the break, end lecture at break start
      if (currentTime < breakStart && currentTime + slotDurationMinutes > breakStart) {
        // Create a shorter lecture slot that ends at break start (if there's meaningful time)
        if (breakStart - currentTime >= 15) {
          allSlots.push({
            start: minutesToTime(currentTime),
            end: minutesToTime(breakStart),
            type: "lecture",
          });
        }
        // Now insert the break
        allSlots.push({
          start: minutesToTime(breakStart),
          end: minutesToTime(breakEnd),
          type: "break",
          label: br.label,
        });
        currentTime = breakEnd;
        breakIndex++;
        continue;
      }
    }

    // Create a normal lecture slot
    const slotEnd = Math.min(currentTime + slotDurationMinutes, endM);
    
    // Check if this slot would be too short
    if (slotEnd - currentTime < 15) {
      break;
    }

    allSlots.push({
      start: minutesToTime(currentTime),
      end: minutesToTime(slotEnd),
      type: "lecture",
    });
    currentTime = slotEnd;
  }

  const slotsPerDayForUi = allSlots.map(({ start, end, type, label }) => ({
    start,
    end,
    ...(type === "break" ? { type: "break", label: label || "Break" } : {}),
  }));

  return { slotsPerDayForUi, dayStart, dayEnd, slotDurationMinutes, breaks };
}

/**
 * Normalize API time to "HH:mm" for <input type="time" />.
 * Sequelize TIME often serializes as 1970-01-01 UTC — use UTC hours/minutes in that case.
 */
export function toTimeInputValue(t) {
  if (t == null || t === "") return "";
  if (typeof t === "string") {
    if (t.includes("T")) {
      const d = new Date(t);
      if (!Number.isNaN(d.getTime()) && d.getUTCFullYear() === 1970) {
        return `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}`;
      }
      const part = t.slice(11, 16);
      return /^\d{2}:\d{2}$/.test(part) ? part : "";
    }
    return t.length >= 5 ? t.slice(0, 5) : t;
  }
  if (t instanceof Date && !Number.isNaN(t.getTime())) {
    const useUtc = t.getUTCFullYear() === 1970;
    const h = useUtc ? t.getUTCHours() : t.getHours();
    const m = useUtc ? t.getUTCMinutes() : t.getMinutes();
    return `${pad2(h)}:${pad2(m)}`;
  }
  return String(t).slice(0, 5);
}

/** Display time as "9:30 AM" (12-hour). */
export function formatTime12h(timeValue) {
  const hhmm = toTimeInputValue(timeValue);
  if (!hhmm) return "";
  const [hStr, mStr] = hhmm.split(":");
  let hours = Number.parseInt(hStr, 10);
  const minutes = mStr ?? "00";
  if (Number.isNaN(hours)) return hhmm;
  const period = hours >= 12 ? "PM" : "AM";
  hours %= 12;
  if (hours === 0) hours = 12;
  return `${hours}:${minutes} ${period}`;
}

/** Display slot range as "9:30 AM - 10:30 AM". */
export function formatTimeRange12h(startTime, endTime) {
  const start = formatTime12h(startTime);
  const end = formatTime12h(endTime);
  if (!start && !end) return "";
  if (!start) return end;
  if (!end) return start;
  return `${start} - ${end}`;
}

/** Normalize FK ids for MUI Select (string matches MenuItem values). */
export function normalizeSlotFk(v) {
  if (v === null || v === undefined || v === "") return "";
  return String(v);
}

/** True when a saved slot overlaps a schedule grid cell [cellStart, cellEnd). */
export function slotOverlapsScheduleCell(slot, cellStart, cellEnd) {
  const ss = timeToMinutes(toTimeInputValue(slot?.startTime));
  const se = timeToMinutes(toTimeInputValue(slot?.endTime));
  const cs = timeToMinutes(toTimeInputValue(cellStart));
  const ce = timeToMinutes(toTimeInputValue(cellEnd));
  if (!Number.isFinite(ss) || !Number.isFinite(ce)) return false;
  const slotEnd = Number.isFinite(se) && se > ss ? se : ss + 1;
  return ss < ce && slotEnd > cs;
}

/** True when a saved slot's start time falls within [cellStart, cellEnd). Used to render a multi-hour slot only in its starting cell. */
export function slotStartsInScheduleCell(slot, cellStart, cellEnd) {
  const ss = timeToMinutes(toTimeInputValue(slot?.startTime));
  const cs = timeToMinutes(toTimeInputValue(cellStart));
  const ce = timeToMinutes(toTimeInputValue(cellEnd));
  if (!Number.isFinite(ss) || !Number.isFinite(cs) || !Number.isFinite(ce)) return false;
  return ss >= cs && ss < ce;
}

/**
 * Add grid rows for lectures whose start time is not covered by any non-break row
 * (e.g. 12:33 when the template marks 12:00–13:00 as lunch break).
 */
export function augmentScheduleRowsWithSlots(baseRows, slotList) {
  if (!Array.isArray(baseRows) || baseRows.length === 0) return baseRows || [];
  const rows = baseRows.map((r) => ({ ...r }));

  const rowBounds = (row) => ({
    start: timeToMinutes(toTimeInputValue(row.start)),
    end: timeToMinutes(toTimeInputValue(row.end)),
    isBreak: String(row.type || "").toLowerCase() === "break",
  });

  for (const slot of slotList || []) {
    const ss = timeToMinutes(toTimeInputValue(slot?.startTime));
    const se = timeToMinutes(toTimeInputValue(slot?.endTime));
    if (!Number.isFinite(ss) || !Number.isFinite(se) || se <= ss) continue;

    const startsInLectureRow = rows.some((row) => {
      const b = rowBounds(row);
      if (b.isBreak) return false;
      return ss >= b.start && ss < b.end;
    });
    if (startsInLectureRow) continue;

    const overlapsLecture = rows.some((row) => {
      const b = rowBounds(row);
      if (b.isBreak) return false;
      return ss < b.end && se > b.start;
    });
    if (overlapsLecture) continue;

    const startStr = toTimeInputValue(slot.startTime);
    const endStr = toTimeInputValue(slot.endTime);
    const exists = rows.some(
      (r) => toTimeInputValue(r.start) === startStr && toTimeInputValue(r.end) === endStr
    );
    if (!exists) {
      rows.push({ start: startStr, end: endStr, type: "lecture", dynamic: true });
    }
  }

  return rows.sort(
    (a, b) => timeToMinutes(toTimeInputValue(a.start)) - timeToMinutes(toTimeInputValue(b.start))
  );
}

/**
 * If teaching interval [startTime, endTime) overlaps a schedule break row, return { label }.
 * Mirrors backend timetableBreakValidation.findBreakConflictForTeachingSlot.
 */
export function findBreakConflictForTeachingSlot(slotsPerDay, startTime, endTime) {
  if (!Array.isArray(slotsPerDay) || slotsPerDay.length === 0) return null;
  const p0 = timeToMinutes(toTimeInputValue(startTime));
  const p1 = timeToMinutes(toTimeInputValue(endTime));
  if (p1 <= p0) return null;
  for (const row of slotsPerDay) {
    if (String(row.type || "").toLowerCase() !== "break") continue;
    const b0 = timeToMinutes(toTimeInputValue(row.start));
    const b1 = timeToMinutes(toTimeInputValue(row.end));
    if (p0 < b1 && b0 < p1) {
      return { label: row.label || "Break" };
    }
  }
  return null;
}

/** Subject view: cell blocked when a class in scope has another subject at the same time. */
export function isSubjectTimetableCellBlocked({ allSlots, day, cellStart, cellEnd, subjectFilter, subjectGroupSlots, slotMatchesSubjectFilter }) {
  if (!Array.isArray(allSlots) || !subjectFilter || typeof slotMatchesSubjectFilter !== "function") return false;
  const classIds = new Set(
    (subjectGroupSlots || [])
      .map((s) => s.classId ?? s.class?.id)
      .filter((id) => id != null)
  );
  if (!classIds.size) return false;
  const dayUpper = String(day || "").toUpperCase();
  return allSlots.some((s) => {
    const cid = s.classId ?? s.class?.id;
    if (!classIds.has(cid)) return false;
    if (String(s.dayOfWeek || s.day || "").toUpperCase() !== dayUpper) return false;
    if (!slotOverlapsScheduleCell(s, cellStart, cellEnd)) return false;
    if (slotMatchesSubjectFilter(s, subjectFilter)) return false;
    return true;
  });
}

/** Teacher view: cell blocked when teacher has another slot at the same time. */
export function isTeacherTimetableCellBlocked({ allSlots, day, cellStart, cellEnd, teacherFilter, teacherGroupSlots, getTeacherName }) {
  if (!Array.isArray(allSlots) || !teacherFilter || typeof getTeacherName !== "function") return false;
  const teacherIds = new Set(
    (teacherGroupSlots || [])
      .map((s) => s.teacherId ?? s.teacher?.id)
      .filter((id) => id != null)
  );
  if (!teacherIds.size) return false;
  const dayUpper = String(day || "").toUpperCase();
  const normFilter = String(teacherFilter || "").trim().toLowerCase();
  return allSlots.some((s) => {
    const tid = s.teacherId ?? s.teacher?.id;
    if (!teacherIds.has(tid)) return false;
    if (String(s.dayOfWeek || s.day || "").toUpperCase() !== dayUpper) return false;
    if (!slotOverlapsScheduleCell(s, cellStart, cellEnd)) return false;
    const name = getTeacherName(s.teacher);
    if (String(name || "").trim().toLowerCase() === normFilter) return false;
    return true;
  });
}
